import { useId, useState } from 'react';
import policyFonts from '../styles/policy-fonts.css?url';
import { useLanguage } from '../core/hooks/use-language';
import { usePageMetadata } from '../core/hooks/use-page-metadata';
import { usePageStylesheet } from '../core/hooks/use-page-stylesheet';
import { STATIC_PAGES, staticPageMetadata } from '../core/seo/static-pages';
import { PolicyDocument } from './policy/policy-document';
import { PolicyFooter } from './policy/policy-footer';
import { PolicyHeader } from './policy/policy-header';
import { PolicyHero } from './policy/policy-hero';
import { PolicySidebar } from './policy/policy-sidebar';
import { PolicyTabs } from './policy/policy-tabs';
import { privacySections, termsSections } from './policy/policy-sections';
import { useActiveSection } from './policy/use-active-section';
import type { PolicyTab } from './policy/policy-tab';


/**
 * The privacy policy and the terms of use — one page with two tabbed documents, as the live site
 * has it: dark banner, sticky tab bar, contents rail and the two documents beside it.
 *
 * **Both documents are always in the DOM**, with the inactive one hidden rather than unmounted. On a
 * page whose purpose is that somebody can find what was agreed, a tab that removes half the text
 * also removes it from the browser's own find-in-page and from anything reading the rendered markup.
 * The cost is one extra document's worth of nodes on a page nobody scrolls twice.
 *
 * **`/legal` and the unprefixed `/policy` both land here**, because both are live URLs today and are
 * in people's history — see the redirects in `AppRoutes`.
 */
export function PolicyPage() {
    const language = useLanguage();
    const [tab, setTab] = useState<PolicyTab>('privacy');
    const privacyPanelId = useId();
    const termsPanelId = useId();

    usePageMetadata(staticPageMetadata(STATIC_PAGES[1], language));
    // `policy.html` loads Montserrat's 500 weight and Playfair Display; `index.html` and `book.html`
    // load neither. See `usePageStylesheet` for why that is reproduced rather than flattened.
    usePageStylesheet(policyFonts);

    const sections = tab === 'privacy' ? privacySections : termsSections;
    const privacyActive = useActiveSection(
        privacySections.map((section) => `privacy-${section.id}`),
        tab === 'privacy'
    );
    const termsActive = useActiveSection(
        termsSections.map((section) => `terms-${section.id}`),
        tab === 'terms'
    );

    function switchTab(next: PolicyTab): void {
        setTab(next);

        // The two documents are different lengths, so staying at the current offset drops the
        // reader into the middle of the other one. The live page does the same, and `smooth` is
        // what the stylesheet asks for everywhere else on this site.
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    return (
        <div className="legal-page">
            <PolicyHeader />
            <PolicyHero />
            <PolicyTabs
                current={tab}
                panelIds={{ privacy: privacyPanelId, terms: termsPanelId }}
                onChange={switchTab}
            />

            <div className="legal-content">
                <div className="legal-body">
                    <PolicySidebar
                        sections={sections}
                        tab={tab}
                        activeId={tab === 'privacy' ? privacyActive : termsActive}
                    />

                    <main>
                        <div id={privacyPanelId} role="tabpanel" hidden={tab !== 'privacy'}>
                            <PolicyDocument sections={privacySections} tab="privacy" />
                        </div>
                        <div id={termsPanelId} role="tabpanel" hidden={tab !== 'terms'}>
                            <PolicyDocument sections={termsSections} tab="terms" />
                        </div>
                    </main>
                </div>
            </div>

            <PolicyFooter />
        </div>
    );
}
